import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import { getLienholderFormData } from '../lienholderForm/utils';
import type { LienholderFormData } from '../../../types';

/**
 * Custom hook to manage lienholder form data from Redux store with local state
 *
 * Provides a local state copy of lienholder data that can optionally sync with Redux store updates.
 * By default, initializes from the store but maintains independent local state.
 * When syncWithStore is true, local state updates whenever the Redux store changes.
 *
 * @param formIndex - The index of the trade-in form to fetch lienholder data for
 * @param syncWithStore - When true, local state syncs with Redux store changes.
 *                        When false (default), local state is independent after initialization.
 * @returns Object containing lienholderData state and setLienholderData setter function
 */
export const useLienholderFormData = (formIndex: number, syncWithStore = false) => {
    const sourceLienholderData = useSelector(tradeInsSelectors.getLienholderFull(formIndex));
    const parsedLienholderData = getLienholderFormData(sourceLienholderData);
    const [lienholderData, setLienholderData] = useState<LienholderFormData>(() => parsedLienholderData);

    useEffect(() => {
        if (syncWithStore) setLienholderData(parsedLienholderData);
    }, [JSON.stringify(parsedLienholderData), syncWithStore]);

    return { lienholderData, setLienholderData };
};
